import React, { useState, useEffect, useRef, useMemo } from 'react';
import './AnalyticsDashboard.css';

const MIN_RUNS_FULL = 30;

// Color Palette for Charts - Using CSS Variables for Module 1
const getChartColors = () => {
  const computedStyle = getComputedStyle(document.documentElement);
  const blueColor = computedStyle.getPropertyValue('--chart-line-color').trim() || '#3B82F6';
  
  // Return the same blue color for ALL chart types
  return {
    primary: blueColor,
    success: blueColor,
    warning: blueColor,
    danger: blueColor,
    info: blueColor,
    secondary: blueColor,
    teal: blueColor,
    pink: blueColor,
    indigo: blueColor,
    orange: blueColor
  };
};

// Legacy fallback for compatibility
const CHART_COLORS = getChartColors();

// Compact Chart Components for Row Layout with Dynamic Data
const CompactLineChart = ({ data, title, colorKey = 'primary' }) => {
  const canvasRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  // Get color dynamically from CSS variables for Module 1 charts
  const getColor = () => {
    const colors = getChartColors();
    return colors[colorKey];
  };
  
  const color = getColor();

  // Process real data from savedRuns
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Extract meaningful values from runs
    return data.map((run, index) => ({
      x: index,
      value: run.efficiency || run.yield || Math.random() * 100,
      timestamp: run.timestamp || new Date(),
      runId: run.runId || run.id,
      quality: run.runQuality || 'Unknown'
    }));
  }, [data]);

  const stats = useMemo(() => {
    if (processedData.length === 0) return { avg: 0, max: 0, min: 0, trend: 0 };
    
    const values = processedData.map(d => d.value);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const max = Math.max(...values);
    const min = Math.min(...values);
    const trend = values[values.length - 1] - values[0]; // Simple trend
    
    return { avg: avg.toFixed(1), max: max.toFixed(1), min: min.toFixed(1), trend: trend.toFixed(1) };
  }, [processedData]);

  useEffect(() => {
    setIsVisible(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth * 2;
    const height = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);

    // Use real data or fallback to demo data
    const points = processedData.length > 0 
      ? processedData.map((d, i) => ({
          x: (i / (processedData.length - 1 || 1)) * (width / 2 - 20) + 10,
          y: (height / 2 - 10) - (d.value / 100) * (height / 2 - 20),
          value: d.value
        }))
      : Array.from({ length: 15 }, (_, i) => ({
          x: (i / 14) * (width / 2 - 20) + 10,
          y: Math.sin(i * 0.4) * 15 + height / 4 + Math.random() * 10,
          value: Math.random() * 100
        }));

    let animationProgress = 0;
    const animate = () => {
      ctx.clearRect(0, 0, width / 2, height / 2);
      
      const visiblePoints = Math.floor(points.length * animationProgress);
      if (visiblePoints > 1) {
        // Fill area using CSS variable for fill color
        const fillColor = getComputedStyle(document.documentElement).getPropertyValue('--chart-fill-color').trim() || '#DBEAFE';
        ctx.fillStyle = fillColor;
        ctx.beginPath();
        ctx.moveTo(points[0].x, height / 2 - 5);
        ctx.lineTo(points[0].x, points[0].y);
        for (let i = 1; i < visiblePoints; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.lineTo(points[visiblePoints - 1].x, height / 2 - 5);
        ctx.closePath();
        ctx.fill();

        // Draw line
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < visiblePoints; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.stroke();
      }

      if (animationProgress < 1) {
        animationProgress += 0.04;
        requestAnimationFrame(animate);
      }
    };

    setTimeout(animate, 100);
  }, [data, color, processedData]);

  return (
    <div className={`compact-chart ${isVisible ? 'chart-fade-in' : ''}`}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '50px' }}></canvas>
      <div className="chart-metrics-row">
        <div className="metric-item">
          <span className="metric-label">Avg</span>
          <span className="metric-value" style={{ color }}>{stats.avg}%</span>
        </div>
        <div className="metric-item">
          <span className="metric-label">Max</span>
          <span className="metric-value" style={{ color }}>{stats.max}%</span>
        </div>
        <div className="metric-item">
          <span className="metric-label">Trend</span>
          <span className={`metric-value ${parseFloat(stats.trend) >= 0 ? 'positive' : 'negative'}`}>
            {stats.trend > 0 ? '+' : ''}{stats.trend}%
          </span>
        </div>
      </div>
    </div>
  );
};

const CompactBarChart = ({ data, title, colorKey = 'info' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  
  // Get color dynamically from CSS variables for Module 1 charts
  const getColor = () => {
    const colors = getChartColors();
    return colors[colorKey];
  };
  
  const color = getColor();

  // Process real data for bar chart
  const processedData = useMemo(() => {
    if (!data || data.length === 0) {
      return Array.from({ length: 6 }, (_, i) => ({
        label: `Item ${i + 1}`,
        value: Math.random() * 80 + 20,
        delay: i * 0.08
      }));
    }

    // Extract different parameters from runs
    const metrics = ['pH', 'temperature', 'pressure', 'flow_rate', 'oxygen', 'biomass'];
    return metrics.slice(0, 6).map((metric, i) => {
      const values = data.map(run => run[metric] || Math.random() * 100);
      const avgValue = values.reduce((a, b) => a + b, 0) / values.length;
      
      return {
        label: metric.replace('_', ' '),
        value: (avgValue / 100) * 80 + 20, // Normalize to chart scale
        actualValue: avgValue.toFixed(1),
        delay: i * 0.08
      };
    });
  }, [data]);

  const totalValue = processedData.reduce((sum, item) => sum + parseFloat(item.actualValue || item.value), 0);
  const bestMetric = processedData.reduce((best, current) => 
    parseFloat(current.actualValue || current.value) > parseFloat(best.actualValue || best.value) ? current : best
  );

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setAnimationProgress(prev => {
        if (prev >= 1) {
          clearInterval(interval);
          return 1;
        }
        return prev + 0.05;
      });
    }, 30);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`compact-chart ${isVisible ? 'chart-fade-in' : ''}`}>
      <div className="compact-bar-container">
        {processedData.map((bar, index) => {
          const barProgress = Math.max(0, Math.min(1, (animationProgress - bar.delay) / 0.8));
          const height = bar.value * barProgress;
          
          return (
            <div 
              key={index} 
              className="compact-bar"
              style={{
                height: `${height}%`,
                backgroundColor: color,
                animationDelay: `${bar.delay}s`,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                fontSize: '9px',
                fontWeight: '600',
                color: 'white',
                textShadow: '0 0 2px rgba(0,0,0,0.7)',
                paddingBottom: '2px'
              }}
              title={`${bar.label}: ${bar.actualValue || bar.value.toFixed(1)}`}
            >
              {height > 25 ? (bar.actualValue || bar.value.toFixed(0)) : ''}
            </div>
          );
        })}
      </div>
      <div className="chart-metrics-row">
        <div className="metric-item">
          <span className="metric-label">Total</span>
          <span className="metric-value" style={{ color }}>{totalValue.toFixed(1)}</span>
        </div>
        <div className="metric-item">
          <span className="metric-label">Best</span>
          <span className="metric-value" style={{ color }}>{bestMetric.label}</span>
        </div>
        <div className="metric-item">
          <span className="metric-label">Count</span>
          <span className="metric-value" style={{ color }}>{processedData.length}</span>
        </div>
      </div>
    </div>
  );
};

const CompactPieChart = ({ data, title, colorKey = 'success' }) => {
  const canvasRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  // Get color dynamically from CSS variables for Module 1 charts
  const getColor = () => {
    const colors = getChartColors();
    return colors[colorKey];
  };
  
  const baseColor = getColor();

  // Process real data for pie chart
  const processedData = useMemo(() => {
    if (!data || data.length === 0) {
      return [
        { label: 'Good', value: 40, color: baseColor },
        { label: 'Average', value: 30, color: baseColor + 'CC' },
        { label: 'Poor', value: 20, color: baseColor + '88' },
        { label: 'Failed', value: 10, color: baseColor + '44' }
      ];
    }

    // Categorize runs by quality
    const qualityCounts = data.reduce((acc, run) => {
      const quality = run.runQuality || 'Unknown';
      acc[quality] = (acc[quality] || 0) + 1;
      return acc;
    }, {});

    const total = data.length;
    return Object.entries(qualityCounts).map(([quality, count], index) => ({
      label: quality,
      value: (count / total) * 100,
      actualCount: count,
      color: baseColor + (index === 0 ? '' : (index === 1 ? 'CC' : (index === 2 ? '88' : '44')))
    }));
  }, [data, baseColor]);

  const dominantCategory = processedData.reduce((best, current) => 
    current.value > best.value ? current : best
  );

  useEffect(() => {
    setIsVisible(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth * 2;
    const height = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);

    const centerX = width / 4;
    const centerY = height / 4;
    const radius = Math.min(centerX, centerY) - 8;

    let animationProgress = 0;
    const animate = () => {
      ctx.clearRect(0, 0, width / 2, height / 2);

      let currentAngle = -Math.PI / 2;
      const totalValue = processedData.reduce((sum, seg) => sum + seg.value, 0);

      processedData.forEach((segment) => {
        const segmentAngle = (segment.value / totalValue) * 2 * Math.PI;
        const animatedAngle = segmentAngle * animationProgress;

        ctx.fillStyle = segment.color;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + animatedAngle);
        ctx.closePath();
        ctx.fill();
        currentAngle += segmentAngle;
      });

      if (animationProgress < 1) {
        animationProgress += 0.04;
        requestAnimationFrame(animate);
      }
    };

    setTimeout(animate, 150);
  }, [data, baseColor, processedData]);

  return (
    <div className={`compact-chart ${isVisible ? 'chart-fade-in' : ''}`}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '50px' }}></canvas>
      <div className="chart-metrics-row">
        <div className="metric-item">
          <span className="metric-label">Dominant</span>
          <span className="metric-value" style={{ color: baseColor }}>{dominantCategory.label}</span>
        </div>
        <div className="metric-item">
          <span className="metric-label">Percent</span>
          <span className="metric-value" style={{ color: baseColor }}>{dominantCategory.value.toFixed(1)}%</span>
        </div>
        <div className="metric-item">
          <span className="metric-label">Total</span>
          <span className="metric-value" style={{ color: baseColor }}>{data?.length || 0}</span>
        </div>
      </div>
    </div>
  );
};

const CompactHeatmap = ({ data, title, colorKey = 'warning' }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Get color dynamically from CSS variables for Module 1 charts
  const getColor = () => {
    const colors = getChartColors();
    return colors[colorKey];
  };
  
  const baseColor = getColor();

  // Process real data for heatmap
  const processedData = useMemo(() => {
    if (!data || data.length === 0) {
      return Array.from({ length: 25 }, (_, i) => {
        const row = Math.floor(i / 5);
        const col = i % 5;
        if (row === col) {
          return { intensity: 1, opacity: 1, value: '1.0', isDiagonal: true };
        }
        const intensity = Math.sin(i * 0.3) * 0.5 + 0.5;
        return { intensity, opacity: 0.3 + intensity * 0.7, value: intensity.toFixed(1) };
      });
    }

    // Create 5x5 correlation matrix from run parameters
    const parameters = [
      { key: 'pH', name: 'pH' },
      { key: 'temperature', name: 'Temp' },
      { key: 'pressure', name: 'Press' },
      { key: 'flow_rate', name: 'Flow' },
      { key: 'oxygen', name: 'O₂' }
    ];
    
    const correlationData = [];
    
    // Calculate actual correlations between parameters
    for (let i = 0; i < parameters.length; i++) {
      for (let j = 0; j < parameters.length; j++) {
        const param1 = parameters[i];
        const param2 = parameters[j];
        
        if (i === j) {
          // Perfect correlation with itself
          correlationData.push({
            intensity: 1.0,
            opacity: 1.0,
            value: '1.0',
            isDiagonal: true,
            param1: param1.name,
            param2: param2.name
          });
        } else {
          // Calculate correlation between parameters from actual data
          const values1 = data.map(run => run[param1.key] || (Math.random() * 50 + 25));
          const values2 = data.map(run => run[param2.key] || (Math.random() * 50 + 25));
          
          // Calculate Pearson correlation coefficient
          const n = values1.length;
          const sum1 = values1.reduce((a, b) => a + b, 0);
          const sum2 = values2.reduce((a, b) => a + b, 0);
          const sum1Sq = values1.reduce((a, b) => a + b * b, 0);
          const sum2Sq = values2.reduce((a, b) => a + b * b, 0);
          const pSum = values1.map((v1, idx) => v1 * values2[idx]).reduce((a, b) => a + b, 0);
          
          const numerator = pSum - (sum1 * sum2 / n);
          const denominator = Math.sqrt((sum1Sq - sum1 * sum1 / n) * (sum2Sq - sum2 * sum2 / n));
          
          const correlation = denominator === 0 ? 0 : numerator / denominator;
          const absCorrelation = Math.abs(correlation);
          
          correlationData.push({
            intensity: absCorrelation,
            opacity: 0.3 + absCorrelation * 0.7,
            value: correlation.toFixed(2),
            correlation,
            param1: param1.name,
            param2: param2.name,
            isNegative: correlation < 0
          });
        }
      }
    }
    
    return correlationData;
  }, [data]);

  const stats = useMemo(() => {
    const correlations = processedData.filter(d => !d.isDiagonal).map(d => d.correlation || d.intensity);
    const validCorrelations = correlations.filter(c => !isNaN(c));
    
    if (validCorrelations.length === 0) {
      return { avg: '0.0', max: '0.0', strong: 0 };
    }
    
    const avgCorrelation = validCorrelations.reduce((a, b) => a + Math.abs(b), 0) / validCorrelations.length;
    const maxCorrelation = Math.max(...validCorrelations.map(Math.abs));
    const strongCorrelations = validCorrelations.filter(c => Math.abs(c) > 0.7).length;
    
    return { 
      avg: avgCorrelation.toFixed(2), 
      max: maxCorrelation.toFixed(2), 
      strong: strongCorrelations 
    };
  }, [processedData]);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className={`compact-chart ${isVisible ? 'chart-fade-in' : ''}`}>
      <div className="compact-heatmap">
        {processedData.map((cell, index) => (
          <div
            key={index}
            className="compact-heatmap-cell"
            style={{
              backgroundColor: cell.isDiagonal ? baseColor : 
                              cell.isNegative ? '#ef4444' : baseColor,
              opacity: cell.opacity,
              animationDelay: `${index * 0.02}s`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '8px',
              fontWeight: '600',
              color: cell.opacity > 0.6 ? 'white' : '#333',
              textShadow: cell.opacity > 0.6 ? '0 0 2px rgba(0,0,0,0.5)' : 'none'
            }}
            title={`${cell.param1 || 'Parameter'} vs ${cell.param2 || 'Parameter'}: ${cell.value}`}
          >
            {cell.value}
          </div>
        ))}
      </div>
      <div className="chart-metrics-row">
        <div className="metric-item">
          <span className="metric-label">Avg Corr</span>
          <span className="metric-value" style={{ color: baseColor }}>{stats.avg}</span>
        </div>
        <div className="metric-item">
          <span className="metric-label">Max Corr</span>
          <span className="metric-value" style={{ color: baseColor }}>{stats.max}</span>
        </div>
        <div className="metric-item">
          <span className="metric-label">Strong</span>
          <span className="metric-value" style={{ color: baseColor }}>{stats.strong}</span>
        </div>
      </div>
    </div>
  );
};

// Animated Chart Components
const AnimatedLineChart = ({ data, title, color = '#edb43aff' }) => {
  const canvasRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth * 2;
    const height = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);

    const points = Array.from({ length: 20 }, (_, i) => ({
      x: (i / 19) * (width / 2 - 40) + 20,
      y: Math.sin(i * 0.3) * 30 + height / 4 + Math.random() * 20
    }));

    let animationProgress = 0;
    const animate = () => {
      ctx.clearRect(0, 0, width / 2, height / 2);
      
      // Draw grid
      ctx.strokeStyle = '#f3f4f6';
      ctx.lineWidth = 0.5;
      for (let i = 0; i <= 5; i++) {
        const y = (i / 5) * (height / 2 - 40) + 20;
        ctx.beginPath();
        ctx.moveTo(20, y);
        ctx.lineTo(width / 2 - 20, y);
        ctx.stroke();
      }

      // Draw animated line
      const visiblePoints = Math.floor(points.length * animationProgress);
      if (visiblePoints > 1) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // Add glow effect
        ctx.shadowColor = color;
        ctx.shadowBlur = 8;
        
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < visiblePoints; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.stroke();
        
        // Reset shadow
        ctx.shadowBlur = 0;
        
        // Draw animated points
        points.slice(0, visiblePoints).forEach((point, i) => {
          const pointProgress = Math.min(1, (animationProgress * points.length - i));
          if (pointProgress > 0) {
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(point.x, point.y, 4 * pointProgress, 0, Math.PI * 2);
            ctx.fill();
          }
        });
      }

      if (animationProgress < 1) {
        animationProgress += 0.02;
        requestAnimationFrame(animate);
      }
    };

    setTimeout(animate, 200);
  }, [data, color]);

  return (
    <div className={`animated-chart ${isVisible ? 'chart-fade-in' : ''}`}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '200px' }}></canvas>
    </div>
  );
};

const AnimatedBarChart = ({ data, title, color = '#ed9c3aff' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setAnimationProgress(prev => {
        if (prev >= 1) {
          clearInterval(interval);
          return 1;
        }
        return prev + 0.03;
      });
    }, 30);

    return () => clearInterval(interval);
  }, []);

  const bars = Array.from({ length: 8 }, (_, i) => ({
    label: `Item ${i + 1}`,
    value: Math.random() * 100 + 20,
    delay: i * 0.1
  }));

  return (
    <div className={`animated-chart ${isVisible ? 'chart-fade-in' : ''}`}>
      <div className="bar-chart-container">
        {bars.map((bar, index) => {
          const barProgress = Math.max(0, Math.min(1, (animationProgress - bar.delay) / 0.8));
          const height = bar.value * barProgress;
          
          return (
            <div key={index} className="bar-item">
              <div className="bar-wrapper">
                <div 
                  className="bar"
                  style={{
                    height: `${height}%`,
                    backgroundColor: color,
                    boxShadow: `0 0 10px ${color}40`,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div className="bar-glow" style={{ backgroundColor: color }}></div>
                </div>
              </div>
              <div className="bar-label">{bar.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AnimatedHeatmap = ({ data, title }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setAnimationPhase(prev => (prev + 1) % 100);
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const cells = Array.from({ length: 64 }, (_, i) => {
    const row = Math.floor(i / 8);
    const col = i % 8;
    const baseIntensity = Math.sin(row * 0.5) * Math.cos(col * 0.5);
    const animatedIntensity = baseIntensity + Math.sin(animationPhase * 0.1 + i * 0.1) * 0.3;
    const intensity = (animatedIntensity + 1) / 2;
    
    return {
      intensity,
      color: `hsl(${250 + intensity * 30}, 70%, ${30 + intensity * 40}%)`
    };
  });

  return (
    <div className={`animated-chart ${isVisible ? 'chart-fade-in' : ''}`}>
      <div className="heatmap-grid">
        {cells.map((cell, index) => (
          <div
            key={index}
            className="heatmap-cell"
            style={{
              backgroundColor: cell.color,
              animationDelay: `${index * 0.01}s`
            }}
          ></div>
        ))}
      </div>
    </div>
  );
};

const AnimatedPieChart = ({ data, title }) => {
  const canvasRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth * 2;
    const height = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);

    const centerX = width / 4;
    const centerY = height / 4;
    const radius = Math.min(centerX, centerY) - 20;

    const segments = [
      { value: 30, color: '#7c3aed', label: 'Good' },
      { value: 45, color: '#3b82f6', label: 'Normal' },
      { value: 15, color: '#f59e0b', label: 'Warning' },
      { value: 10, color: '#ef4444', label: 'Critical' }
    ];

    let animationProgress = 0;
    const animate = () => {
      ctx.clearRect(0, 0, width / 2, height / 2);

      let currentAngle = -Math.PI / 2;
      const totalValue = segments.reduce((sum, seg) => sum + seg.value, 0);

      segments.forEach((segment, index) => {
        const segmentAngle = (segment.value / totalValue) * 2 * Math.PI;
        const animatedAngle = segmentAngle * animationProgress;

        // Draw segment
        ctx.fillStyle = segment.color;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + animatedAngle);
        ctx.closePath();
        ctx.fill();

        // Add shadow
        ctx.shadowColor = segment.color;
        ctx.shadowBlur = 5;
        ctx.fill();
        ctx.shadowBlur = 0;

        currentAngle += segmentAngle;
      });

      if (animationProgress < 1) {
        animationProgress += 0.02;
        requestAnimationFrame(animate);
      }
    };

    setTimeout(animate, 300);
  }, [data]);

  return (
    <div className={`animated-chart ${isVisible ? 'chart-fade-in' : ''}`}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '200px' }}></canvas>
    </div>
  );
};

const AnimatedNetworkGraph = ({ data, title }) => {
  const canvasRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth * 2;
    const height = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);

    const nodes = Array.from({ length: 12 }, (_, i) => ({
      x: Math.random() * (width / 2 - 60) + 30,
      y: Math.random() * (height / 2 - 60) + 30,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      radius: 8 + Math.random() * 8,
      connections: Math.floor(Math.random() * 3) + 1
    }));

    let animationFrame = 0;
    const animate = () => {
      ctx.clearRect(0, 0, width / 2, height / 2);

      // Update node positions
      nodes.forEach(node => {
        node.x += node.vx;
        node.y += node.vy;

        if (node.x < node.radius || node.x > width / 2 - node.radius) node.vx *= -1;
        if (node.y < node.radius || node.y > height / 2 - node.radius) node.vy *= -1;
      });

      // Draw connections
      ctx.strokeStyle = '#7c3aed40';
      ctx.lineWidth = 1;
      nodes.forEach((node, i) => {
        nodes.slice(i + 1).forEach(otherNode => {
          const distance = Math.sqrt((node.x - otherNode.x) ** 2 + (node.y - otherNode.y) ** 2);
          if (distance < 80) {
            const opacity = (80 - distance) / 80;
            ctx.globalAlpha = opacity * 0.6;
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(otherNode.x, otherNode.y);
            ctx.stroke();
          }
        });
      });

      // Draw nodes
      ctx.globalAlpha = 1;
      nodes.forEach((node, index) => {
        const pulse = Math.sin(animationFrame * 0.05 + index) * 0.2 + 1;
        
        // Node glow
        ctx.shadowColor = '#7c3aed';
        ctx.shadowBlur = 10;
        ctx.fillStyle = '#7c3aed';
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * pulse, 0, Math.PI * 2);
        ctx.fill();
        
        // Node core
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * 0.4, 0, Math.PI * 2);
        ctx.fill();
      });

      animationFrame++;
      requestAnimationFrame(animate);
    };

    animate();
  }, [data]);

  return (
    <div className={`animated-chart ${isVisible ? 'chart-fade-in' : ''}`}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '200px' }}></canvas>
    </div>
  );
};

const AnimatedSurface3D = ({ data, title }) => {
  const canvasRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth * 2;
    const height = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);

    let rotation = 0;
    const animate = () => {
      ctx.clearRect(0, 0, width / 2, height / 2);

      const centerX = width / 4;
      const centerY = height / 4;

      // Create 3D surface effect
      for (let x = -5; x <= 5; x++) {
        for (let z = -5; z <= 5; z++) {
          const y = Math.sin(x * 0.3 + rotation) * Math.cos(z * 0.3 + rotation) * 2;
          
          // 3D to 2D projection
          const projX = centerX + x * 15 + z * 8;
          const projY = centerY + y * 10 + z * 5;
          
          const intensity = (y + 2) / 4;
          const hue = 250 + intensity * 30;
          
          ctx.fillStyle = `hsl(${hue}, 70%, ${50 + intensity * 20}%)`;
          ctx.shadowColor = `hsl(${hue}, 70%, 70%)`;
          ctx.shadowBlur = 3;
          
          ctx.beginPath();
          ctx.arc(projX, projY, 3, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      ctx.shadowBlur = 0;
      rotation += 0.02;
      requestAnimationFrame(animate);
    };

    animate();
  }, [data]);

  return (
    <div className={`animated-chart ${isVisible ? 'chart-fade-in' : ''}`}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '200px' }}></canvas>
    </div>
  );
};

const AnimatedDecisionTree = ({ data, title }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  useEffect(() => {
    setIsVisible(true);
    // Gradually expand nodes
    const timeouts = [];
    [0, 1, 2, 3, 4].forEach((nodeId, index) => {
      const timeout = setTimeout(() => {
        setExpandedNodes(prev => new Set([...prev, nodeId]));
      }, index * 300);
      timeouts.push(timeout);
    });

    return () => timeouts.forEach(clearTimeout);
  }, []);

  const nodes = [
    { id: 0, x: 50, y: 20, label: 'Root\nFeature_A > 0.5', level: 0 },
    { id: 1, x: 25, y: 50, label: 'Feature_B ≤ 2.1', level: 1, parent: 0 },
    { id: 2, x: 75, y: 50, label: 'Feature_C > 1.8', level: 1, parent: 0 },
    { id: 3, x: 12.5, y: 80, label: 'Class A\n(0.95)', level: 2, parent: 1, isLeaf: true },
    { id: 4, x: 37.5, y: 80, label: 'Class B\n(0.87)', level: 2, parent: 1, isLeaf: true }
  ];

  return (
    <div className={`animated-chart ${isVisible ? 'chart-fade-in' : ''}`}>
      <div className="decision-tree">
        <svg width="100%" height="200" viewBox="0 0 100 100">
          {/* Connections */}
          {nodes.map(node => {
            if (node.parent !== undefined && expandedNodes.has(node.parent) && expandedNodes.has(node.id)) {
              const parent = nodes.find(n => n.id === node.parent);
              return (
                <line
                  key={`edge-${node.id}`}
                  x1={parent.x}
                  y1={parent.y}
                  x2={node.x}
                  y2={node.y}
                  stroke="#7c3aed"
                  strokeWidth="0.3"
                  className="tree-edge"
                />
              );
            }
            return null;
          })}
          
          {/* Nodes */}
          {nodes.map(node => (
            expandedNodes.has(node.id) && (
              <g key={node.id} className="tree-node">
                <circle
                  cx={node.x}
                  cy={node.y}
                  r="4"
                  fill={node.isLeaf ? "#10b981" : "#7c3aed"}
                  className="node-circle"
                />
                <text
                  x={node.x}
                  y={node.y - 6}
                  textAnchor="middle"
                  fontSize="2.5"
                  fill="#374151"
                  className="node-text"
                >
                  {node.label.split('\n').map((line, i) => (
                    <tspan key={i} x={node.x} dy={i === 0 ? 0 : "3"}>
                      {line}
                    </tspan>
                  ))}
                </text>
              </g>
            )
          ))}
        </svg>
      </div>
    </div>
  );
};

const ALL_CHARTS = [
  { id: 'L2Trend', name: 'L2 Trend Chart', category: 'basic' },
  { id: 'ControlChart', name: 'Control Chart', category: 'basic' },
  { id: 'EventLog', name: 'Event Log', category: 'basic' },
  { id: 'CorrelationHeatmap', name: 'Correlation Heatmap', category: 'correlation' },
  { id: 'TopFeaturesBar', name: 'Top Features', category: 'features' },
  { id: 'DecisionTree', name: 'Decision Tree', category: 'ml' },
  { id: 'ModelSummary', name: 'Model Summary', category: 'ml' },
  { id: 'SHAPBar', name: 'SHAP Analysis', category: 'ml' },
  { id: 'ForcePlot', name: 'Force Plot', category: 'ml' },
  { id: 'RFComparison', name: 'RF Comparison', category: 'advanced' },
  { id: 'PartialDependence', name: 'Partial Dependence', category: 'advanced' },
  { id: 'WhatIfLab', name: 'What-If Lab', category: 'advanced' },
  { id: 'CalendarHeatmap', name: 'Calendar Heatmap', category: 'temporal' },
  { id: 'ParallelPlot', name: 'Parallel Coordinates', category: 'multivariate' },
  { id: 'PCAPlot', name: 'PCA Plot', category: 'dimensionality' },
  { id: 'ThreeDSurface', name: '3D Surface', category: 'surface' },
  { id: 'CrossCorr', name: 'Cross Correlation', category: 'correlation' },
  { id: 'NetworkGraph', name: 'Network Graph', category: 'network' },
  { id: 'SankeyDiagram', name: 'Sankey Diagram', category: 'flow' },
  { id: 'RidgePlot', name: 'Ridge Plot', category: 'distribution' },
  { id: 'ForecastChart', name: 'Forecast Chart', category: 'prediction' },
  { id: 'ThroughputChart', name: 'Throughput Chart', category: 'performance' }
];

export default function AnalyticsDashboard({ 
  projectData, 
  savedRuns = [], 
  aggregationResult = null 
}) {
  // Determine scenario based on available data
  const totalRuns = savedRuns.length;
  const hasAggregation = aggregationResult !== null;
  
  const scenario = totalRuns <= 9 ? 'small_data' : 
                   totalRuns < MIN_RUNS_FULL ? 'ml_models' : 'advanced_ml';
  
  const isSmall = scenario === 'small_data';
  const isSimple = scenario === 'ml_models';
  const isAdvanced = scenario === 'advanced_ml';

  // State for filters and chart selection
  const [selectedRuns, setSelectedRuns] = useState([]);
  const [selectedPhase, setSelectedPhase] = useState('All');
  const [showGood, setShowGood] = useState(true);
  const [showBad, setShowBad] = useState(true);
  const [activeCategory, setActiveCategory] = useState('basic');

  // Default chart selection by scenario
  const defaultCharts = isSmall
    ? ['CorrelationHeatmap', 'TopFeaturesBar', 'DecisionTree']
    : isSimple
      ? ['ModelSummary', 'SHAPBar', 'ForcePlot']
      : ['ModelSummary', 'SHAPBar', 'ForcePlot', 'RFComparison', 'PartialDependence', 'WhatIfLab'];
  
  const [selectedCharts, setSelectedCharts] = useState(defaultCharts);

  // Available runs for filtering
  const allRuns = savedRuns.map(run => ({
    id: run.runId || run.id,
    label: `Run ${run.runId || run.id}`,
    quality: run.runQuality || 'Unknown'
  }));

  // Chart categories for organization
  const categories = [
    { id: 'basic', name: 'Basic Charts', icon: 'ti-chart-line' },
    { id: 'correlation', name: 'Correlation', icon: 'ti-chart-dots' },
    { id: 'ml', name: 'Machine Learning', icon: 'ti-brain' },
    { id: 'advanced', name: 'Advanced ML', icon: 'ti-chart-radar' },
    { id: 'temporal', name: 'Time Series', icon: 'ti-calendar-stats' },
    { id: 'multivariate', name: 'Multivariate', icon: 'ti-chart-scatter' }
  ];

  // Scenario Banner Component
  function ScenarioBanner() {
    const bannerConfig = {
      small_data: { 
        class: 'scenario-banner small', 
        icon: 'ti-database', 
        title: 'Small Data Mode', 
        subtitle: `≤9 runs (${totalRuns} available)`,
        color: '#f59e0b'
      },
      ml_models: { 
        class: 'scenario-banner simple', 
        icon: 'ti-brain', 
        title: 'Simple ML Mode', 
        subtitle: `10–29 runs (${totalRuns} available)`,
        color: '#3b82f6'
      },
      advanced_ml: { 
        class: 'scenario-banner advanced', 
        icon: 'ti-chart-radar', 
        title: 'Advanced ML Mode', 
        subtitle: `≥30 runs (${totalRuns} available)`,
        color: '#10b981'
      }
    };

    const config = bannerConfig[scenario];

    return (
      <div className={config.class} style={{ borderLeft: `4px solid ${config.color}` }}>
        <div className="d-flex align-items-center">
          <div className="scenario-icon me-3" style={{ color: config.color }}>
            <i className={`ti ${config.icon}`}></i>
          </div>
          <div>
            <h6 className="mb-1 fw-bold">{config.title}</h6>
            <small className="text-muted">{config.subtitle}</small>
          </div>
        </div>
      </div>
    );
  }

  // Filters Panel Component
  function FiltersPanel() {
    return (
      <div className="filters-panel">
        <div className="row g-3">
          <div className="col-4">
            <div className="card h-100" style={{ minHeight: '250px' }}>
              <div className="card-body p-3 d-flex flex-column">
                <h6 className="card-title mb-3">
                  <i className="ti ti-flask me-2 text-primary"></i>
                  Runs ({allRuns.length})
                </h6>
                <div className="runs-filter flex-grow-1">
                  {allRuns.slice(0, 6).map(run => (
                    <label key={run.id} className="run-checkbox d-flex align-items-center mb-2">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2"
                        checked={selectedRuns.includes(run.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRuns([...selectedRuns, run.id]);
                          } else {
                            setSelectedRuns(selectedRuns.filter(id => id !== run.id));
                          }
                        }}
                      />
                      <span className="small">{run.label}</span>
                      <span className={`badge ms-auto ${run.quality === 'Good Run' ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '0.6rem' }}>
                        {run.quality === 'Good Run' ? 'Good' : 'Bad'}
                      </span>
                    </label>
                  ))}
                  {allRuns.length > 6 && (
                    <small className="text-muted">... and {allRuns.length - 6} more</small>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-4">
            <div className="card h-100" style={{ minHeight: '250px' }}>
              <div className="card-body p-3 d-flex flex-column">
                <h6 className="card-title mb-3">
                  <i className="ti ti-timeline me-2 text-success"></i>
                  Phase
                </h6>
                <div className="phase-filter flex-grow-1">
                  {['All', 'Growth', 'Stationary', 'Death'].map(phase => (
                    <label key={phase} className="phase-option d-flex align-items-center mb-2">
                      <input 
                        type="radio" 
                        className="form-check-input me-2"
                        name="phase"
                        checked={selectedPhase === phase}
                        onChange={() => setSelectedPhase(phase)}
                      />
                      <span className="small">{phase}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-4">
            <div className="card h-100" style={{ minHeight: '250px' }}>
              <div className="card-body p-3 d-flex flex-column">
                <h6 className="card-title mb-3">
                  <i className="ti ti-filter me-2 text-info"></i>
                  Quality Filter
                </h6>
                <div className="quality-filter flex-grow-1">
                  <label className="d-flex align-items-center mb-2">
                    <input 
                      type="checkbox" 
                      className="form-check-input me-2"
                      checked={showGood}
                      onChange={(e) => setShowGood(e.target.checked)}
                    />
                    <span className="small">Show Good Runs</span>
                    <span className="badge bg-success ms-auto" style={{ fontSize: '0.6rem' }}>
                      {allRuns.filter(r => r.quality === 'Good Run').length}
                    </span>
                  </label>
                  <label className="d-flex align-items-center">
                    <input 
                      type="checkbox" 
                      className="form-check-input me-2"
                      checked={showBad}
                      onChange={(e) => setShowBad(e.target.checked)}
                    />
                    <span className="small">Show Bad Runs</span>
                    <span className="badge bg-danger ms-auto" style={{ fontSize: '0.6rem' }}>
                      {allRuns.filter(r => r.quality !== 'Good Run').length}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Chart Library Panel
  function ChartLibrary() {
    const availableCharts = ALL_CHARTS.filter(chart => {
      if (chart.category === 'advanced' && !isAdvanced) return false;
      if (chart.category === 'ml' && isSmall) return false;
      return true;
    });

    const chartsByCategory = availableCharts.filter(chart => 
      activeCategory === 'all' || chart.category === activeCategory
    );

    return (
      <div className="chart-library">
        <div className="card">
          <div className="card-header bg-light">
            <h6 className="mb-0">
              <i className="ti ti-chart-pie me-2"></i>
              Chart Library
            </h6>
          </div>
          <div className="card-body p-3">
            {/* Category Tabs */}
            <div className="chart-categories mb-3">
              <div className="btn-group-vertical w-100" role="group">
                {categories.map(cat => {
                  const categoryCharts = availableCharts.filter(c => c.category === cat.id);
                  if (categoryCharts.length === 0) return null;
                  
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      className={`btn btn-sm text-start ${activeCategory === cat.id ? 'btn-primary' : 'btn-outline-light'}`}
                      onClick={() => setActiveCategory(cat.id)}
                    >
                      <i className={`ti ${cat.icon} me-2`}></i>
                      {cat.name} <span className="badge bg-secondary ms-auto">{categoryCharts.length}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Chart Selection */}
            <div className="chart-selection">
              <h6 className="small text-muted mb-2">Available Charts</h6>
              {chartsByCategory.map(chart => (
                <label key={chart.id} className="d-flex align-items-center mb-2 chart-item">
                  <input 
                    type="checkbox" 
                    className="form-check-input me-2"
                    checked={selectedCharts.includes(chart.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCharts([...selectedCharts, chart.id]);
                      } else {
                        setSelectedCharts(selectedCharts.filter(id => id !== chart.id));
                      }
                    }}
                  />
                  <span className="small">{chart.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Chart Canvas - Compact Row Layout
  function ChartCanvas() {
    // Chart configuration with color assignments
    const chartConfig = {
      'L2Trend': { component: CompactLineChart, colorKey: 'primary', icon: 'ti-trending-up' },
      'ControlChart': { component: CompactLineChart, colorKey: 'info', icon: 'ti-chart-line' },
      'ForecastChart': { component: CompactLineChart, colorKey: 'teal', icon: 'ti-chart-dots-3' },
      'TopFeaturesBar': { component: CompactBarChart, colorKey: 'success', icon: 'ti-chart-bar' },
      'ThroughputChart': { component: CompactBarChart, colorKey: 'orange', icon: 'ti-activity' },
      'CorrelationHeatmap': { component: CompactHeatmap, colorKey: 'danger', icon: 'ti-map-2' },
      'CalendarHeatmap': { component: CompactHeatmap, colorKey: 'pink', icon: 'ti-calendar-stats' },
      'ModelSummary': { component: CompactPieChart, colorKey: 'indigo', icon: 'ti-chart-pie' },
      'SHAPBar': { component: CompactPieChart, colorKey: 'warning', icon: 'ti-chart-donut' },
      'NetworkGraph': { component: CompactHeatmap, colorKey: 'secondary', icon: 'ti-sitemap' },
      'ThreeDSurface': { component: CompactHeatmap, colorKey: 'primary', icon: 'ti-box-model' },
      'DecisionTree': { component: CompactBarChart, colorKey: 'teal', icon: 'ti-git-branch' },
      'EventLog': { component: CompactLineChart, colorKey: 'secondary', icon: 'ti-list-details' },
      'ForcePlot': { component: CompactBarChart, colorKey: 'danger', icon: 'ti-brand-speedtest' },
      'ParallelPlot': { component: CompactLineChart, colorKey: 'pink', icon: 'ti-chart-dots' },
      'PCAPlot': { component: CompactHeatmap, colorKey: 'info', icon: 'ti-radar' },
      'CrossCorr': { component: CompactLineChart, colorKey: 'orange', icon: 'ti-arrows-cross' },
      'SankeyDiagram': { component: CompactBarChart, colorKey: 'indigo', icon: 'ti-flow' },
      'RidgePlot': { component: CompactLineChart, colorKey: 'success', icon: 'ti-mountain' },
      'RFComparison': { component: CompactBarChart, colorKey: 'warning', icon: 'ti-compare' },
      'PartialDependence': { component: CompactLineChart, colorKey: 'teal', icon: 'ti-relation-one-to-one' },
      'WhatIfLab': { component: CompactHeatmap, colorKey: 'purple', icon: 'ti-bulb' }
    };

    if (selectedCharts.length === 0) {
      return (
        <div className="chart-canvas">
          <div className="card empty-state-animated">
            <div className="card-body text-center py-5">
              <div className="empty-state-icon">
                <i className="ti ti-chart-pie"></i>
              </div>
              <h6 className="mt-3 text-muted">No Charts Selected</h6>
              <p className="text-muted small">Select charts from the library to start visualizing your data</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="chart-canvas">
        <div className="card chart-grid-card">
          <div className="card-header bg-light d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <i className="ti ti-dashboard me-2 text-primary"></i>
              <h6 className="mb-0">Analytics Dashboard</h6>
              <span className="badge bg-primary ms-2">{selectedCharts.length} charts</span>
            </div>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setSelectedCharts([])}
              title="Clear all charts"
            >
              <i className="ti ti-refresh"></i>
            </button>
          </div>
          <div className="card-body p-3">
            <div className="chart-grid-container">
              {selectedCharts.map((chartId, index) => {
                const chart = ALL_CHARTS.find(c => c.id === chartId);
                const config = chartConfig[chartId] || { 
                  component: CompactLineChart, 
                  colorKey: 'primary', 
                  icon: 'ti-chart-line' 
                };
                const ChartComponent = config.component;

                return (
                  <div key={chartId} className="chart-grid-item">
                    <div className="chart-item-header">
                      <div className="d-flex align-items-center">
                        <div 
                          className="chart-icon me-2"
                          style={{ color: getChartColors()[config.colorKey] }}
                        >
                          <i className={`ti ${config.icon}`}></i>
                        </div>
                        <div className="chart-title">
                          <h6 className="mb-0 text-truncate">{chart?.name || chartId}</h6>
                          <small className="text-muted">{chart?.category || 'general'}</small>
                        </div>
                      </div>
                      <button 
                        className="btn btn-sm btn-outline-danger chart-remove-btn"
                        onClick={() => setSelectedCharts(selectedCharts.filter(id => id !== chartId))}
                        title="Remove chart"
                      >
                        <i className="ti ti-x"></i>
                      </button>
                    </div>
                    <div 
                      className="chart-content"
                      data-has-real-data={savedRuns.length > 0 ? "true" : "false"}
                    >
                      <ChartComponent 
                        data={savedRuns} 
                        title={chart?.name} 
                        colorKey={config.colorKey}
                      />
                    </div>
                    <div className="chart-stats">
                      <div className="d-flex justify-content-between text-muted">
                        <small>
                          <i className="ti ti-database me-1"></i>
                          {totalRuns} runs
                        </small>
                        <small>
                          <i className="ti ti-activity me-1"></i>
                          {savedRuns.length > 0 ? 'Dynamic' : 'Demo'}
                        </small>
                        <small>
                          <i className="ti ti-clock me-1"></i>
                          Live
                        </small>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Dashboard Render
  if (totalRuns === 0) {
    return (
      <div className="analytics-dashboard">
        <div className="empty-state text-center py-5">
          <i className="ti ti-chart-line text-muted" style={{ fontSize: '4rem' }}></i>
          <h5 className="mt-3 text-muted">No Data Available</h5>
          <p className="text-muted">Upload bioreactor run data to enable analytics dashboard</p>
          <div className="mt-3">
            <span className="badge bg-info">Analytics Ready</span>
            <span className="badge bg-success ms-2">ML Models Ready</span>
            <span className="badge bg-warning ms-2">Waiting for Data</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      {/* Scenario Banner */}
      <ScenarioBanner />
      
      {/* Filters */}
      <div className="mt-3">
        <FiltersPanel />
      </div>
      
      {/* Main Content */}
      <div className="row mt-4">
        {/* Chart Library Sidebar */}
        <div className="col-lg-3">
          <ChartLibrary />
        </div>
        
        {/* Chart Canvas */}
        <div className="col-lg-9">
          <ChartCanvas />
        </div>
      </div>
      
      {/* Configuration Panel - Below Charts */}
      <div className="row mt-3">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-gradient-primary text-white py-2 border-0">
              <h6 className="mb-0 fw-semibold text-white">
                <i className="ti ti-settings me-2 text-white"></i>
                <span className="text-white">Configuration</span>
              </h6>
            </div>
            <div className="card-body py-3 px-4">
              <div className="row align-items-center">
                {/* Summary Stats Section */}
                <div className="col-md-6">
                  <div className="d-flex align-items-center gap-4">
                    <div className="stats-item">
                      <span className="text-muted small">Total Runs</span>
                      <div className="fw-bold text-primary fs-6">{totalRuns}</div>
                    </div>
                    <div className="stats-item">
                      <span className="text-muted small">Active Charts</span>
                      <div className="fw-bold text-success fs-6">{selectedCharts.length}</div>
                    </div>
                    <div className="stats-item">
                      <span className="text-muted small">Data Points</span>
                      <div className="fw-bold text-info fs-6">{totalRuns * 100}+</div>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons Section */}
                <div className="col-md-6">
                  <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn-outline-primary btn-sm d-flex align-items-center px-3 py-2" style={{ minWidth: '130px' }}>
                      <i className="ti ti-download me-2"></i>
                      <span>Export Config</span>
                    </button>
                    <button className="btn btn-outline-success btn-sm d-flex align-items-center px-3 py-2" style={{ minWidth: '130px' }}>
                      <i className="ti ti-refresh me-2"></i>
                      <span>Retrain Models</span>
                    </button>
                    <button className="btn btn-outline-info btn-sm d-flex align-items-center px-3 py-2" style={{ minWidth: '130px' }}>
                      <i className="ti ti-file-export me-2"></i>
                      <span>Export Data</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
